// Brand-aware data plate location knowledge base — client-side query layer.
//
// This module queries the `data_plate_locations` Supabase table — Casavio's
// proprietary moat. Each row teaches the scan flow where the data plate is for
// a specific brand+type combination, plus what visual cues the AI should look
// for during spec extraction.
//
// Row shape (matches the public.data_plate_locations table):
//   {
//     id, appliance_type, brand,
//     photos: [{ role, instruction, required }, ...],
//     spec_extraction_hints: ['Series 600S', 'HE', ...],
//     notes,
//     source_photo_ids,      // RESERVED for upcoming intel_photos integration
//     contributed_by,        // RESERVED — will FK to auth.users when intel tool ships
//     confidence, last_updated, created_at, deleted_at
//   }
//
// Session B+ readiness notes (deferred — do not implement now):
//   - An `intel_photos` table will hold photos contributed by advisors / users.
//     When a contributor uploads a photo, the upload pipeline will:
//       1. Extract zip code from EXIF (5-digit only, never finer).
//       2. Strip ALL exact GPS coordinates from the EXIF block.
//       3. Store the photo with the coarsened metadata.
//       4. Link the photo to one or more data_plate_locations entries via
//          source_photo_ids (uuid[] column already in place).
//   - `contributed_by` will FK to auth.users once the intel tool opens to
//     advisor contributions. RLS will be expanded at that point — today the
//     write policy restricts to Kenneth's UUID only.
//
// All exports are async even when current behavior is synchronous-ish, so call
// sites don't need refactoring when behavior changes (e.g., caching, fallback
// to remote registry, telemetry on lookups).

import { supabase } from '../supabase'

const SELECT_COLUMNS =
  'id, appliance_type, brand, photos, spec_extraction_hints, notes, ' +
  'source_photo_ids, confidence, contributed_by, last_updated, created_at'

/**
 * Look up brand-aware scan instructions for an appliance.
 *
 * @param {string} applianceType e.g. 'washer-top-load', 'dryer'
 * @param {string} brand         e.g. 'kenmore' (must already be normalized)
 * @returns {Promise<object|null>} the matching row, or null if no match
 */
export async function getInstructionsFor(applianceType, brand) {
  if (!applianceType || !brand) return null
  const { data, error } = await supabase
    .from('data_plate_locations')
    .select(SELECT_COLUMNS)
    .eq('appliance_type', applianceType)
    .eq('brand', brand)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle()
  if (error) {
    console.warn('[dataPlateLocations] lookup failed', { applianceType, brand, error: error.message })
    return null
  }
  return data || null
}

/**
 * Generic 2-photo fallback when no brand-specific entry exists. Returns an
 * object with the same shape as a real row so callers can treat both paths
 * uniformly.
 *
 * @param {string} applianceType
 * @returns {Promise<object>} always returns — never null
 */
export async function getGenericInstructions(applianceType) {
  return {
    id: null,
    appliance_type: applianceType || null,
    brand: null,
    photos: [
      {
        role: 'front',
        instruction: 'Take a photo of the front of the appliance.',
        required: true,
      },
      {
        role: 'data-plate',
        instruction: 'Take a photo of the data plate (usually inside a door, on the back, or on a side panel).',
        required: true,
      },
    ],
    spec_extraction_hints: [],
    notes: null,
    source_photo_ids: [],
    confidence: 'generic',
    contributed_by: null,
    last_updated: null,
    created_at: null,
  }
}

/**
 * Normalize a brand string for matching: trim + lowercase. Use this on any
 * value pulled from the AI extraction or user input before passing to
 * getInstructionsFor.
 *
 * @param {string|null|undefined} rawBrand
 * @returns {string} normalized brand, or '' if input was empty
 */
export function normalizeBrand(rawBrand) {
  if (rawBrand == null) return ''
  return String(rawBrand).trim().toLowerCase()
}

/**
 * Build an ordered list of `appliance_type` candidate keys to try in the
 * data_plate_locations lookup. The AI returns broad types like "Washer" or
 * "Refrigerator" — the table sometimes keys on subtype ("washer-top-load",
 * "refrigerator-side-by-side"). This helper combines the AI's type with any
 * visual-notes signals to produce candidate keys in best-to-worst order.
 *
 * The caller should try each candidate in order and use the first hit.
 *
 * @param {{type?:string|null, visual_notes?:string|null}} extracted AI response
 * @returns {string[]} ordered candidate keys (may be empty if no type)
 */
export function applianceTypeCandidates(extracted) {
  const base = (extracted?.type || '').trim().toLowerCase()
  if (!base) return []
  const notes = (extracted?.visual_notes || '').toLowerCase()
  const out = []
  if (base === 'washer') {
    if (notes.includes('top-load') || notes.includes('top load') || notes.includes('top-loading') || notes.includes('top loading')) out.push('washer-top-load')
    if (notes.includes('front-load') || notes.includes('front load') || notes.includes('front-loading') || notes.includes('front loading')) out.push('washer-front-load')
  } else if (base === 'refrigerator') {
    if (notes.includes('side-by-side') || notes.includes('side by side')) out.push('refrigerator-side-by-side')
    if (notes.includes('french door')) out.push('refrigerator-french-door')
    if (notes.includes('top freezer') || notes.includes('top-freezer')) out.push('refrigerator-top-freezer')
    if (notes.includes('bottom freezer') || notes.includes('bottom-freezer')) out.push('refrigerator-bottom-freezer')
  } else if (base === 'dryer') {
    if (notes.includes('front-load') || notes.includes('front load')) out.push('dryer-front-load')
    if (notes.includes('top-load') || notes.includes('top load')) out.push('dryer-top-load')
  }
  out.push(base)
  return out
}

/**
 * Try each candidate appliance_type key in order; return the first match.
 *
 * @param {string[]} candidates ordered list of appliance_type keys
 * @param {string}   brand      normalized brand (use normalizeBrand)
 * @returns {Promise<object|null>}
 */
export async function lookupBestInstructions(candidates, brand) {
  if (!Array.isArray(candidates) || candidates.length === 0 || !brand) return null
  for (const t of candidates) {
    const row = await getInstructionsFor(t, brand)
    if (row) return row
  }
  return null
}
